const STORAGE_KEY = "theme";

/**
 * 主题切换组件
 * 支持 Dark/Light 模式切换，并集成了 Web Animations API 和 View Transitions API
 * 采用 Unpoly 编译器模式实现
 */
export class LightDarkSwitcher {
  private isDark: boolean = false;
  private iconWrapper: HTMLElement | null;
  private container: HTMLButtonElement;

  constructor(container: HTMLElement) {
    this.container = container as HTMLButtonElement;
    this.iconWrapper = container.querySelector(".icon-wrapper");
    this.init();
  }

  /**
   * 初始化组件状态，从 HTML 根节点的 class 中读取当前主题
   */
  private init() {
    this.isDark = document.documentElement.classList.contains("dark");
    // 初始化时同步一次主题色和图标
    this.updateMetaThemeColor();
    this.updateIcons();

    // 绑定点击事件
    up.on(this.container, "click", () => this.toggle());
  }

  /**
   * 切换主题模式
   * 包含退出动画、模式应用及进入动画
   */
  public async toggle() {
    if (!this.iconWrapper) return;

    // 1. 退出动画：向上滑出并旋转淡出
    const exitAnim = this.iconWrapper.animate(
      [
        { opacity: "1", transform: "translateY(0) rotate(0deg)" },
        { opacity: "0", transform: "translateY(-20px) rotate(45deg)" },
      ],
      { duration: 150, easing: "ease-in", fill: "forwards" },
    );
    await exitAnim.finished;
    exitAnim.commitStyles();
    exitAnim.cancel();

    // 2. 应用模式切换
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    // @ts-expect-error - View Transitions API 较新，类型定义可能不全
    if (document.startViewTransition && !prefersReducedMotion) {
      // @ts-expect-error
      document.startViewTransition(() => this.applyTheme());
    } else {
      this.applyTheme();
    }

    // 3. 进入动画：从下方滑入并旋转淡入
    const enterAnim = this.iconWrapper.animate(
      [
        { opacity: "0", transform: "translateY(20px) rotate(-45deg)" },
        { opacity: "1", transform: "translateY(0) rotate(0deg)" },
      ],
      { duration: 150, easing: "ease-out", fill: "forwards" },
    );
    await enterAnim.finished;
    enterAnim.commitStyles();
    enterAnim.cancel();
  }

  /**
   * 执行主题切换的 DOM 和状态更新
   */
  private applyTheme() {
    this.isDark = !this.isDark;
    document.documentElement.classList.toggle("dark", this.isDark);
    localStorage.setItem(STORAGE_KEY, this.isDark ? "dark" : "light");

    // 更新按钮内部图标 (如果不是通过 CSS 控制)
    this.updateIcons();

    // 动态同步主题色彩到 meta 标签
    this.updateMetaThemeColor();

    // 触发全局主题改变事件
    up.emit("theme:changed", { isDark: this.isDark });
  }

  /**
   * 更新图标内容
   */
  private updateIcons() {
    if (!this.iconWrapper) return;
    const useElement = this.iconWrapper.querySelector("use") as SVGUseElement;
    useElement.href.baseVal = this.isDark ? "#icon-moon" : "#icon-sun";

    // 同时更新 aria-label (在外部按钮上)
    const label = this.isDark
      ? this.container.dataset.labelLight
      : this.container.dataset.labelDark;
    if (label) {
      this.container.setAttribute("aria-label", label);
    }
  }

  /**
   * 内部方法：从 CSS 变量读取背景色并更新 meta[name="theme-color"]
   * 确保 JS 与 CSS 变量 (如 --bg-main) 保持同步，作为单一事实来源
   */
  private updateMetaThemeColor() {
    // 确保读取的是此时此刻生效的计算样式
    const computedStyle = getComputedStyle(document.documentElement);
    const themeColor = computedStyle.getPropertyValue("--bg-main").trim();

    const themeColorMetas = document.querySelectorAll<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (themeColorMetas.length > 0 && themeColor) {
      themeColorMetas.forEach((meta) => {
        meta.content = themeColor;
      });
    }
  }

  destroy() {
    // Unpoly 会自动清理绑定在 this.container 上的事件
  }

  /**
   * 注册编译器
   * @param selector 选择器
   */
  static compiler(selector: string) {
    up.compiler(selector, (element: Element) => {
      const switcher = new LightDarkSwitcher(element as HTMLElement);
      return () => switcher.destroy();
    });
  }
}
